/**
 * SplitComparison - Side-by-side or top-bottom image comparison
 *
 * Uses CSS Grid to show two images in a split view.
 * Supports horizontal (side-by-side) and vertical (top-bottom) modes.
 * Uses AdaptivePhoto for intelligent resolution selection.
 *
 * Feature: 081-photo-versioning (T053)
 */

import { cn } from '@/lib/utils';
import { AdaptivePhoto, type AdaptivePhotoProps } from '@/components/photos/AdaptivePhoto';

export interface SplitComparisonProps {
  /** Photo object for the "before" image */
  beforePhoto: AdaptivePhotoProps['photo'];
  /** Photo object for the "after" image */
  afterPhoto: AdaptivePhotoProps['photo'];
  /** Alt text for before image */
  beforeAlt?: string;
  /** Alt text for after image */
  afterAlt?: string;
  /** Label for before image (e.g., "v1") */
  beforeLabel?: string;
  /** Label for after image (e.g., "v2") */
  afterLabel?: string;
  /** Split direction */
  direction?: 'horizontal' | 'vertical';
  /** Optional className */
  className?: string;
}

/**
 * Split view comparison component using CSS Grid
 */
export function SplitComparison({
  beforePhoto,
  afterPhoto,
  beforeAlt = 'Before',
  afterAlt = 'After',
  beforeLabel = 'Before',
  afterLabel = 'After',
  direction = 'horizontal',
  className,
}: SplitComparisonProps) {
  const isHorizontal = direction === 'horizontal';

  return (
    <div
      className={cn(
        'grid gap-2 w-full h-full',
        isHorizontal ? 'grid-cols-2' : 'grid-rows-2',
        className
      )}
    >
      {/* Before image */}
      <div className="relative overflow-hidden bg-surface-2">
        <AdaptivePhoto
          key={`before-${beforePhoto.id}`}
          photo={beforePhoto}
          alt={beforeAlt}
          className="w-full h-full"
          imgClassName="w-full h-full object-contain"
          trackingSource="version_compare"
          preferStorageUrl
        />
        <div className="absolute top-4 left-4 z-10 pointer-events-none">
          <span className="px-2 py-1 bg-black/70 text-white text-xs rounded">
            {beforeLabel}
          </span>
        </div>
      </div>

      {/* After image */}
      <div className="relative overflow-hidden bg-surface-2">
        <AdaptivePhoto
          key={`after-${afterPhoto.id}`}
          photo={afterPhoto}
          alt={afterAlt}
          className="w-full h-full"
          imgClassName="w-full h-full object-contain"
          trackingSource="version_compare"
          preferStorageUrl
        />
        <div className="absolute top-4 left-4 z-10 pointer-events-none">
          <span className="px-2 py-1 bg-black/70 text-white text-xs rounded">
            {afterLabel}
          </span>
        </div>
      </div>
    </div>
  );
}
